import { useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress, useTheme } from '@mui/material'
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom'
import { Box, Button, Container, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import PortfolioHeatStress from '../components/climateAnalytics/portfolio/PortfolioHeatStress'
import PortfolioExtremeWind from '../components/climateAnalytics/portfolio/PortfolioExtremeWind'
import PortfolioFlood from '../components/climateAnalytics/portfolio/PortfolioFlood'
import PortfolioWaterRisk from '../components/climateAnalytics/portfolio/PortfolioWaterRisk'
import PortfolioWildfire from '../components/climateAnalytics/portfolio/PortfolioWildfire'
import siteMap from '../siteMap'
import { useClimateHazards } from '../hooks/analytics'
import { EmissionTier } from '../components/climateAnalytics/common'
import EmissionTierSelector from '../components/climateAnalytics/common/EmissionTierSelector'
import { ClimateAnalyticsPermissions, PermissionGate } from 'balkerne-core/roles'

const ClimateAnalytics = () => {
  const history = useHistory()
  const location = useLocation()
  const hazardMatch = useRouteMatch<{ hazard?: string }>(`${siteMap.ClimateAnalytics.path}/:hazard`)
  const theme = useTheme()
  const { data: hazards, isLoading: isHazardsLoading } = useClimateHazards()
  const [emissionTier, setEmissionTier] = useState<EmissionTier>(() => {
    const param = new URLSearchParams(location.search).get('emission')
    if (param === EmissionTier.LOW || param === EmissionTier.MODERATE || param === EmissionTier.HIGH) {
      return param as EmissionTier
    }
    return EmissionTier.HIGH
  })
  const [selectedHazardCode, setSelectedHazardCode] = useState<string | null>(null)

  const hazardFromRoute = hazardMatch?.params?.hazard
  const emissionFromQuery = useMemo(() => {
    const param = new URLSearchParams(location.search).get('emission')
    if (param === EmissionTier.LOW || param === EmissionTier.MODERATE || param === EmissionTier.HIGH) {
      return param as EmissionTier
    }
    return null
  }, [location.search])

  const lastSearchRef = useRef(location.search)

  const hazard = useMemo(() => {
    return hazards.find(h => h.name === selectedHazardCode)
  }, [hazards, selectedHazardCode])

  useEffect(() => {
    if (!hazards.length) return
    const fallbackHazard = hazards[0]?.name ?? null
    const nextHazard = hazards.find(h => h.name === hazardFromRoute)?.name ?? fallbackHazard
    if (!nextHazard) return

    if (selectedHazardCode !== nextHazard) {
      setSelectedHazardCode(nextHazard)
    }

    const targetPath = siteMap.ClimateAnalytics.getPath(nextHazard, emissionTier)
    const currentPath = `${location.pathname}${location.search}`
    if (currentPath !== targetPath) {
      history.replace(targetPath)
    }
  }, [emissionTier, hazards, hazardFromRoute, history, location.pathname, location.search, selectedHazardCode])

  useEffect(() => {
    if (lastSearchRef.current === location.search) return
    lastSearchRef.current = location.search
    if (emissionFromQuery && emissionFromQuery !== emissionTier) {
      setEmissionTier(emissionFromQuery)
    }
  }, [emissionFromQuery, emissionTier, location.search])

  const renderContent = useMemo(() => {
    switch (selectedHazardCode) {
      case 'fld':
        return <PortfolioFlood emissionTier={emissionTier} />
      case 'hs':
        return <PortfolioHeatStress emissionTier={emissionTier} />
      case 'tc':
        return <PortfolioExtremeWind emissionTier={emissionTier} />
      case 'wr':
        return <PortfolioWaterRisk emissionTier={emissionTier} />
      case 'wf':
        return <PortfolioWildfire emissionTier={emissionTier} />
      default:
        return null
    }
  }, [selectedHazardCode, emissionTier])

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar + 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'saturate(180%) blur(6px)',
        }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
          {/* Eyebrow + Primary actions (row 1) */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            py={1.5}>
            <Typography variant="overline" color="text.secondary">
              Climate Analytics
            </Typography>

            <Stack direction="row" spacing={1}>
              <PermissionGate action={ClimateAnalyticsPermissions.Modify}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => history.push(siteMap.ClimateAnalyticsConfigure.path)}>
                  Configure Analysis
                </Button>
              </PermissionGate>
              <Button
                size="small"
                variant="contained"
                onClick={() => history.push(siteMap.ClimateAnalyticsExport.path)}>
                Export Data
              </Button>
            </Stack>
          </Stack>

          {/* Title + Controls (row 2) */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
            pb={2}>
            <Typography variant="h4" fontWeight={800}>
              {hazard ? hazard.display_name : 'Hazard'}
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="hazard-label">Hazard</InputLabel>
                <Select
                  labelId="hazard-label"
                  value={selectedHazardCode ?? ''}
                  label="Hazard"
                  disabled={isHazardsLoading}
                  onChange={e => {
                    const value = e.target.value as string
                    if (!value) return
                    setSelectedHazardCode(value)
                    history.push(siteMap.ClimateAnalytics.getPath(value, emissionTier))
                  }}>
                  {hazards.map(h => (
                    <MenuItem key={h.id} value={h.name}>
                      {h.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <EmissionTierSelector
                value={emissionTier}
                onChange={setEmissionTier}
                orientation="horizontal"
                fullWidth={false}
                size="compact"
              />
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      {isHazardsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          {renderContent}
        </Container>
      )}
    </Box>
  )
}

export default ClimateAnalytics
